import './style.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as PIXI from 'pixi.js';
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { GameScene } from '@/core/types/global.types';
import { initBattleBus } from '@/events/Events';
import { BattleScene } from '@/view/scenes/BattleScene';
import { WorldScene } from '@/view/scenes/WorldScene';
import { RootUI } from '@/view/ui/RootUI';
import { SoundManager } from '@/core/battle/SoundManager';
import { LoadingOverlay } from '@/core/utils/common.utils';
import { useGlobalStore } from '@/store/useGlobalStore';



// Реєстрація плагінів GSAP
gsap.registerPlugin(MotionPathPlugin, PixiPlugin);
PixiPlugin.registerPIXI(PIXI);

// Інтерфейс для сцен, щоб не писати 'as any'
interface IGameScene extends PIXI.Container {
    init?(): Promise<void> | void;
    resize?(width: number, height: number): void;
}

// Глобальна змінна для запобігання подвійній ініціалізації
// let appInstance: PIXI.Application | null = null;
let rootReact: ReactDOM.Root | null = null;


async function initGame() {
    // 1. ПЕРЕВІРКА ГЛОБАЛЬНОГО ІНСТАНСУ
    if (globalThis.__PIXI_APP__) {
        console.warn('HMR: Killing old Pixi instance...');
        const oldApp = globalThis.__PIXI_APP__;
        
        // Зупиняємо рендер та тікер ПЕРЕД дестроєм
        oldApp.ticker.stop();
        oldApp.stop();
        
        oldApp.destroy(true, { 
            children: true, 
            texture: true, 
        });
        globalThis.__PIXI_APP__ = undefined;
    }
    // 1. Якщо додаток вже існує (наприклад, після швидкого релоаду), повертаємо його
    // if (appInstance) return;

    // 2. Створюємо або знаходимо контейнери (Fix для HMR)
    let appContainer = document.getElementById('app-container');
    let pixiWrapper = document.getElementById('pixi-wrapper');
    let uiRoot = document.getElementById('ui-root');

    if (!appContainer) {
        appContainer = document.createElement('div');
        appContainer.id = 'app-container';
        
        pixiWrapper = document.createElement('div');
        pixiWrapper.id = 'pixi-wrapper';
        
        uiRoot = document.createElement('div');
        uiRoot.id = 'ui-root';

        

        appContainer.appendChild(pixiWrapper);
        appContainer.appendChild(uiRoot);
        document.body.appendChild(appContainer);
    }

    // 3. Ініціалізація Pixi
    const app = new PIXI.Application();
    
    // appInstance = app; // Фіксуємо інстанс
    // appInstance.__PIXI_APP__ = app
    
    
    await app.init({ 
        // preference: 'webgl',
        resizeTo: pixiWrapper as HTMLElement, 
        // resolution: 1,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        backgroundColor: 0x1a1714,
        autoStart: false,
        antialias: false,
    });
    app.ticker.stop();
    globalThis.__PIXI_APP__ = app;
    (window as any).app = app; // Для дебагу в консолі

    // Очищення старого канвасу (Fix для HMR)
    const oldCanvas = pixiWrapper!.querySelector('canvas');
    if (oldCanvas) oldCanvas.remove();

    pixiWrapper!.appendChild(app.canvas);
    

    // const fpsTicker = new PIXI.Text({
//     text: 'FPS: 0',
//     style: {
//         fontFamily: 'Arial',
//         fontSize: 14,
//         fill: 0xffffff,
//         backgroundColor: 'rgba(0, 0, 0, 0.5)'
//     }
// });
// fpsTicker.x = 10;
// fpsTicker.y = 10;
    // ========================================================================
    // ЛОГІКА СЦЕН
    // ========================================================================

    let currentPixiScene: IGameScene | null = null;
    let accumulatedTime = 0;
    
    const syncPixiScene = async (newScene: GameScene) => {
        LoadingOverlay.show();
        if (app.ticker.started) {
            app.ticker.stop();
        }

        
        // A. Очищення
        if (currentPixiScene) {
            console.info(`[Scene] Destroying old scene`);
            currentPixiScene.destroy({ children: true }); 
            app.stage.removeChild(currentPixiScene);
            currentPixiScene = null;
        }

        app.resize(); 

        console.log(`[Scene] Init ${newScene}. Screen: ${app.screen.width}x${app.screen.height}`);

        if (newScene === GameScene.BATTLE) {
            await SoundManager.init();
            initBattleBus();
            accumulatedTime = 0;
            app.ticker.start();
            currentPixiScene = new BattleScene(app);
        } else if (newScene === GameScene.CITY) {
            return;
        } else {
            currentPixiScene = new WorldScene(app);
        }

        app.stage.addChild(currentPixiScene);
        // app.stage.addChild(fpsTicker);

        // D. Init
        if (currentPixiScene.init) {
            await currentPixiScene.init();   
        }

        await LoadingOverlay.hide();
        
    };

    useGlobalStore.getState().setChangeSceneFn(syncPixiScene);

    gsap.ticker.remove(gsap.updateRoot);
    gsap.ticker.sleep();

    gsap.config({ autoSleep: 0, force3D: true }); 

    app.ticker.add(() => {
        const dt = Math.min(app.ticker.deltaMS, 100) / 1000;
        accumulatedTime += dt;
        
        gsap.updateRoot(accumulatedTime);
    });

    PIXI.Ticker.shared.stop();
    PIXI.Ticker.system.stop();

    const blockMouseTracking = () => {
        const originalAddEventListener = EventTarget.prototype.addEventListener;
        EventTarget.prototype.addEventListener = function(type: string, fn: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
            if (type === 'pointermove' || type === 'mousemove') {
                const fnStr = typeof fn === 'function' ? fn.toString() : '';
                const fnName = typeof fn === 'function' ? fn.name : '';
                
                const isReactInternal = fnStr.includes('react') || 
                    fnName.includes('bound') ||
                    (this === document) || 
                    (this === window);

                const isHubElement = this instanceof Element && 
                    (this as Element).closest('.hub-viewport');

                if (isReactInternal && !isHubElement) {
                    return;
                }
            }
            return originalAddEventListener.call(this, type, fn, options);
        };
    };

    blockMouseTracking();

    // 4. Запуск React
    // Якщо рут вже був створений (HMR), використовуємо його, або створюємо новий
    if (!rootReact && uiRoot) {
        

        // 1. Створюємо "фільтр" на стадії Capture
        // uiRoot.addEventListener('pointermove', (e) => {
        //     e.stopPropagation(); 
        //     e.stopImmediatePropagation(); 
        // }, { 
        //     capture: true, 
        //     passive: true // Passive можна лишити для швидкості
        // });

        rootReact = ReactDOM.createRoot(uiRoot);
    }
    
    if (rootReact) {
        rootReact.render(
            <React.StrictMode>
                {/* Передаємо функцію як пропс - це і є наш міст */}
                <RootUI/>
            </React.StrictMode>
        );
        
    }
    // syncPixiScene(GameScene.BATTLE);
    // Для дебагу в консолі
    // (window as any).app = app; 
    

    (window as any).PIXI = PIXI;
    (window as any).app = app;
    (window as any).gsap = gsap;
}

initGame().catch(console.error);